import { Text } from "@certn/cascadia/foundations/typography/Text";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { CSSProperties, isValidElement, ReactNode } from "react";
import { VisuallyHidden } from "react-aria";
import {
  ColumnProps,
  Key,
  ResizableTableContainer,
  TableBodyProps,
} from "react-aria-components";
import { useTranslation } from "react-i18next";

import { Cell, textAlignment } from "./assets/Cell";
import { Column } from "./assets/Column";
import { Row } from "./assets/Row";
import { Table, TableProps } from "./assets/Table";
import { TableBody } from "./assets/TableBody";
import { TableHeader } from "./assets/TableHeader";
import { Skeleton } from "../../patterns/skeleton/Skeleton";
import { mq } from "../utils";

export interface ColumnDefinition<T> {
  allowsSorting?: ColumnProps["allowsSorting"];
  children?: ColumnDefinition<T>[];
  defaultWidth?: ColumnProps["defaultWidth"];
  id?: ColumnProps["id"];
  isAction?: boolean;
  isRowHeader?: ColumnProps["isRowHeader"];
  key: string;
  maxWidth?: ColumnProps["maxWidth"];
  minWidth?: ColumnProps["minWidth"];
  name?: React.ReactNode;
  render?(item: T): React.ReactNode;
  textValue?: string;
  width?: ColumnProps["width"];
  /** Shows a drag handle on the column edge. `width` or `defaultWidth` is the starting size. */
  allowsResizing?: boolean;
  testID?: string;
  textAlignment?: textAlignment;
  displayRightBorder?: boolean;
  onClick?: (item: T) => void;
  /**
   * Lets an in-cell stretched overlay cover the whole row. Required because
   * cells clip overflow and rows are not positioned by default.
   */
  isStretchedRowLink?: boolean;
}
interface Props<T> extends TableProps {
  columns: ColumnDefinition<T>[];
  rowKey?: string;
  rows: T[];
  highlightedRows?: Key[];
  testId?: string;
  isLoading?: boolean;
  renderEmptyState?: TableBodyProps<T>["renderEmptyState"];
  minWidth?: string;
  disableScrolling?: CSSProperties["overflowX"];
  skeletonRows?: number;
}

export function DataTable<T extends { id: Key }>(props: Props<T>) {
  const { t } = useTranslation();

  const {
    columns,
    rows,
    renderEmptyState,
    highlightedRows = [],
    testId = "data-table",
    rowKey = "id",
    minWidth = "934px",
    disableScrolling = "auto",
    skeletonRows = 5,
  } = props;
  const hasStretchedRowLink = columns.some(
    (column) => column.isStretchedRowLink
  );
  const hasResizableColumn = columns.some((column) => column.allowsResizing);

  // Create skeleton rows when loading and no data
  const displayRows =
    props.isLoading && rows.length === 0
      ? Array.from(
          { length: skeletonRows },
          (_, index) =>
            ({
              id: `skeleton-${index}` as Key,
              _isSkeleton: true,
            }) as T & { _isSkeleton?: boolean }
        )
      : rows;

  const selectionBehavior =
    props.selectionBehavior ??
    (props.selectionMode === "multiple" ? "toggle" : undefined);

  return (
    <div
      data-testid={testId}
      className={styles.scrollContainer}
      style={{
        overflowX: disableScrolling || "auto",
      }}
    >
      <div
        className={css(
          mq({
            minWidth: [undefined, undefined, minWidth],
          })
        )}
      >
        <ResizableTable enabled={hasResizableColumn}>
          <Table
            {...props}
            hasStretchedRowLink={hasStretchedRowLink}
            selectionBehavior={selectionBehavior}
            onRowAction={!props.isLoading ? props.onRowAction : undefined}
          >
            <TableHeader columns={columns}>
              {(column) => {
                return (
                  <Column
                    textAlignment={column.textAlignment}
                    displayRightBorder={column.displayRightBorder}
                    allowsResizing={column.allowsResizing}
                    id={column.id || column.key}
                    allowsSorting={column.allowsSorting}
                    defaultWidth={
                      column.allowsResizing
                        ? column.defaultWidth ?? column.width
                        : column.defaultWidth ??
                          (column.isAction ? 48 : undefined)
                    }
                    isRowHeader={column.isRowHeader}
                    key={column.key}
                    maxWidth={column.maxWidth}
                    minWidth={column.minWidth}
                    width={column.allowsResizing ? undefined : column.width}
                    textValue={
                      column.textValue ||
                      (typeof column.name === "string"
                        ? column.name
                        : column.key)
                    }
                    data-testid={column?.testID || undefined}
                  >
                    {column.name || (
                      <VisuallyHidden>{column.textValue}</VisuallyHidden>
                    )}
                  </Column>
                );
              }}
            </TableHeader>
            <TableBody
              items={displayRows}
              renderEmptyState={(renderProps) => {
                return (
                  <div className={styles.empty}>
                    {renderEmptyState ? (
                      renderEmptyState(renderProps)
                    ) : (
                      <Text
                        style="global/body-secondary"
                        align="center"
                        color="text-weak"
                      >
                        {t("No results found.")}
                      </Text>
                    )}
                  </div>
                );
              }}
            >
              {(item) => {
                const highlightedRowBackgroundColor = "background-warning-weak";
                const isHighlighted = highlightedRows.includes(item.id);
                const backgroundColor = isHighlighted
                  ? highlightedRowBackgroundColor
                  : undefined;
                const isSkeleton = (item as any)?._isSkeleton;

                return (
                  <Row
                    key={item[rowKey as keyof typeof item] as Key}
                    id={item[rowKey as keyof typeof item] as Key}
                    columns={columns}
                    data-testid="data-table-row"
                    backgroundColor={backgroundColor}
                    hasStretchedRowLink={hasStretchedRowLink}
                  >
                    {({
                      key,
                      render,
                      isAction,
                      textAlignment,
                      displayRightBorder,
                      onClick,
                      isStretchedRowLink,
                    }) => {
                      const itemValue = item[key as keyof typeof item];
                      const testID = `${testId}-${slugify(key)}-cell`;

                      // Render skeleton content for loading rows
                      if (isSkeleton) {
                        return (
                          <Cell
                            textAlignment={textAlignment}
                            displayRightBorder={displayRightBorder}
                            data-testid={testID}
                            isAction={isAction}
                            isStretchedRowLink={isStretchedRowLink}
                            key={key}
                          >
                            <Skeleton width="80%" height="20px" />
                          </Cell>
                        );
                      }

                      if (render) {
                        return (
                          <Cell
                            textAlignment={textAlignment}
                            displayRightBorder={displayRightBorder}
                            data-testid={testID}
                            isAction={isAction}
                            isStretchedRowLink={isStretchedRowLink}
                            key={key}
                            onClick={onClick && (() => onClick(item))}
                          >
                            {render(item)}
                          </Cell>
                        );
                      }

                      if (
                        isValidElement(itemValue) ||
                        typeof itemValue === "string"
                      ) {
                        return (
                          <Cell
                            data-testid={testID}
                            isAction={isAction}
                            isStretchedRowLink={isStretchedRowLink}
                            textAlignment={textAlignment}
                            displayRightBorder={displayRightBorder}
                            onClick={onClick && (() => onClick(item))}
                          >
                            {itemValue}
                          </Cell>
                        );
                      }

                      return (
                        <Cell
                          data-testid={testID}
                          isAction={isAction}
                          isStretchedRowLink={isStretchedRowLink}
                          displayRightBorder={displayRightBorder}
                          onClick={onClick && (() => onClick(item))}
                        >
                          {JSON.stringify(itemValue)}
                        </Cell>
                      );
                    }}
                  </Row>
                );
              }}
            </TableBody>
          </Table>
        </ResizableTable>
      </div>
    </div>
  );
}

function ResizableTable({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  if (!enabled) {
    return children;
  }

  return (
    <ResizableTableContainer className={styles.resizableContainer}>
      {children}
    </ResizableTableContainer>
  );
}

const styles = {
  scrollContainer: css({ width: "100%" }),
  resizableContainer: css({ width: "100%" }),
  empty: css({
    padding: "var(--space-6)",
    minHeight: 288,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
};
